/**
 * Discount Price Display
 * Shows strike-through pricing on product and collection pages when practitioner or promo discounts are active
 */

class DiscountPriceDisplay {
  constructor() {
    // Prevent multiple initializations
    if (window.discountPriceDisplayInitialized) {
      return;
    }
    window.discountPriceDisplayInitialized = true;

    this.practitionerDiscountCodes = {
      "045SZKVFF6BB": 5,
      KXJYTA5VWPYD: 10,
      "99G5194EHJC6": 15,
      "8564JCC6QFE6": 20,
      SJBVP381QA6D: 25,
      RQW7SNJQPN00: 30,
      FV3P6BSNNNXR: 40,
    };

    this.isProcessing = false;
    this.init();
  }

  init() {
    const discountInfo = this.getActiveDiscount();

    if (discountInfo) {
      this.applyDiscountPricing(discountInfo);
      this.applyCartLineItemDiscounts(discountInfo);

      // Re-apply on variant changes only
      const variantChangeHandler = () => {
        if (this.isProcessing) return;
        this.isProcessing = true;

        // Clear processed flags on variant change
        document.querySelectorAll("[data-discount-processed]").forEach((el) => {
          delete el.dataset.discountProcessed;
        });
        this.applyDiscountPricing(discountInfo);

        setTimeout(() => {
          this.isProcessing = false;
        }, 500);
      };

      document.addEventListener("variant:change", variantChangeHandler, {
        once: false,
      });
    }
  }

  getCookie(name) {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  getPromoDiscountInfo() {
    const promoCode = this.getCookie("promo_code");
    const promoType = this.getCookie("promo_type");
    const promoAmount = this.getCookie("promo_amount");
    if (promoCode && promoCode !== "" && promoType && promoAmount) {
      return {
        type: "promo",
        code: promoCode,
        discountType: promoType,
        amount: parseFloat(promoAmount),
      };
    }
    return null;
  }

  getPractitionerDiscountInfo() {
    const practitionerCode = this.getCookie("discount_code");
    if (practitionerCode && this.practitionerDiscountCodes[practitionerCode]) {
      return {
        type: "practitioner",
        code: practitionerCode,
        discountType: "percentage",
        amount: this.practitionerDiscountCodes[practitionerCode],
      };
    }
    return null;
  }

  getActiveDiscount() {
    const promoDiscount = this.getPromoDiscountInfo();
    const practitionerDiscount = this.getPractitionerDiscountInfo();

    if (promoDiscount) {
      promoDiscount.practitionerFallback = practitionerDiscount || null;
      return promoDiscount;
    }

    if (practitionerDiscount) {
      practitionerDiscount.practitionerFallback = practitionerDiscount;
      return practitionerDiscount;
    }

    return null;
  }

  calculateDiscountedPrice(originalPrice, discountInfo) {
    if (discountInfo.discountType === "percentage") {
      return Math.round(originalPrice * (1 - discountInfo.amount / 100));
    } else if (discountInfo.discountType === "fixed") {
      // Fixed amount is in dollars, convert to cents
      const discountCents = Math.round(discountInfo.amount * 100);
      return Math.max(0, originalPrice - discountCents);
    }
    return originalPrice;
  }

  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    const [whole, decimal] = dollars.split(".");
    return `<span class="price__prefix">$</span>${whole}<sup class="price__suffix">.${decimal}</sup>`;
  }

  removeCurrencyCodeFromHtml(html) {
    if (!html) {
      return html;
    }
    return html
      .replace(/\s*[A-Z]{3}\s*(<\/bdi>)/g, "$1")
      .replace(/\s*[A-Z]{3}\s*(<\/price-money>)/g, "$1")
      .replace(/\s*[A-Z]{3}\s*$/g, "");
  }

  stripCurrencyCodesFromPrices() {
    const priceElements = document.querySelectorAll("price-money bdi");
    priceElements.forEach((bdi) => {
      bdi.innerHTML = this.removeCurrencyCodeFromHtml(bdi.innerHTML);
    });
    const priceTexts = document.querySelectorAll("price-money");
    priceTexts.forEach((priceMoneyEl) => {
      priceMoneyEl.innerHTML = this.removeCurrencyCodeFromHtml(
        priceMoneyEl.innerHTML
      );
    });
  }

  applyDiscountPricing(discountInfo) {
    // Don't run on cart page to avoid conflicts
    if (document.body.classList.contains("template-cart")) {
      return;
    }

    // Ensure any existing currency codes are removed from price displays
    this.stripCurrencyCodesFromPrices();

    // Find all price elements
    const priceElements = document.querySelectorAll(".price");

    priceElements.forEach((priceEl) => {
      // Skip if already processed
      if (priceEl.dataset.discountProcessed) {
        return;
      }

      try {
        // Find the regular price item
        const regularPriceItem = priceEl.querySelector(".price-item--regular");
        if (!regularPriceItem) return;

        // Get the price money element
        const priceMoneyEl = regularPriceItem.querySelector("price-money bdi");
        if (!priceMoneyEl) return;

        // Extract price value (remove currency symbols and parse)
        const priceText = priceMoneyEl.textContent.trim();
        const priceValue = parseFloat(priceText.replace(/[^0-9.]/g, ""));

        if (isNaN(priceValue)) return;

        // Convert to cents
        const priceCents = Math.round(priceValue * 100);

        // Calculate discounted price
        const discountedPriceCents = this.calculateDiscountedPrice(
          priceCents,
          discountInfo
        );

        // Only show discount if there's an actual difference
        if (discountedPriceCents >= priceCents) return;

        // Mark as processed
        priceEl.dataset.discountProcessed = "true";

        // Check if we're in a sale context (compare_at_price exists)
        const saleContext = priceEl.classList.contains("price--on-sale");

        if (saleContext) {
          // Product already on sale - modify the sale price
          const salePriceItem = priceEl.querySelector(
            ".price__sale .price-item--sale"
          );
          if (salePriceItem) {
            const salePriceMoneyEl =
              salePriceItem.querySelector("price-money bdi");
            if (salePriceMoneyEl) {
              const salePriceText = salePriceMoneyEl.textContent.trim();
              const salePriceValue = parseFloat(
                salePriceText.replace(/[^0-9.]/g, "")
              );
              const salePriceCents = Math.round(salePriceValue * 100);
              const discountedSalePriceCents = this.calculateDiscountedPrice(
                salePriceCents,
                discountInfo
              );

              // Wrap original sale price in strikethrough
              const originalSalePrice = this.removeCurrencyCodeFromHtml(
                salePriceMoneyEl.innerHTML
              );
              salePriceMoneyEl.innerHTML = this.removeCurrencyCodeFromHtml(
                salePriceMoneyEl.innerHTML
              );
              salePriceItem.innerHTML = `
              <span class="price-diagonal-strike">
                <price-money class="discount-price">${originalSalePrice}</price-money>
              </span>
              <price-money class="discount-price">${this.formatMoney(
                discountedSalePriceCents
              )}</price-money>
            `;
            }
          }
        } else {
          // Regular price - add strike-through and discounted price
          const originalPrice = this.removeCurrencyCodeFromHtml(
            priceMoneyEl.innerHTML
          );
          priceMoneyEl.innerHTML = this.removeCurrencyCodeFromHtml(
            priceMoneyEl.innerHTML
          );
          regularPriceItem.innerHTML = `
          <span class="price-diagonal-strike">
            <price-money class="discount-price">${originalPrice}</price-money>
          </span>
          <price-money class="discount-price">${this.formatMoney(
            discountedPriceCents
          )}</price-money>
        `;
        }
      } catch (error) {
        console.error("Error applying product discount:", error);
      }
    });
  }

  applyCartLineItemDiscounts(discountInfo) {
    // Only run on cart page
    if (!document.body.classList.contains("template-cart")) {
      return;
    }

    console.log("Applying cart line item discounts", discountInfo);

    // Find all cart items (rows in the cart)
    const cartItems = document.querySelectorAll("tbody tr.cart-item");
    console.log("Found cart items:", cartItems.length);

    cartItems.forEach((cartItem, index) => {
      // Skip if already processed
      if (cartItem.dataset.discountProcessed) {
        console.log("Item", index, "already processed");
        return;
      }

      // Prevent processing errors from breaking the loop
      try {
        // Find the quantity input to get quantity
        const quantityInput = cartItem.querySelector(
          'input[name="updates[]"], .quantity__input'
        );
        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
        console.log("Item", index, "quantity:", quantity);

        // Check for selling plan (volume discount) from data attribute
        const sellingPlanId = cartItem.dataset.sellingPlanId || null;
        const isVolumePlan =
          sellingPlanId === "4433281161" || sellingPlanId === "4433313929";
        console.log("Item", index, "selling plan ID:", sellingPlanId);

        // Find the old price (original unit price before volume discount)
        const oldPriceElement = cartItem.querySelector(
          ".cart-item__old-price price-money bdi, .variant-item__old-price price-money bdi"
        );
        console.log("Item", index, "old price element:", oldPriceElement);

        // Find ALL price wrappers (mobile and desktop)
        const priceWrappers = cartItem.querySelectorAll(
          ".cart-item__price-wrapper"
        );
        if (!priceWrappers || priceWrappers.length === 0) {
          console.log("Item", index, "no price wrapper found");
          return;
        }

        console.log(
          "Item",
          index,
          "found",
          priceWrappers.length,
          "price wrappers"
        );

        let currentLinePrice = null;
        let originalUnitPrice = null;
        let volumeDiscountMultiplier = 1.0;
        let hasVolumeDiscount = false;

        // Determine volume discount multiplier
        if (sellingPlanId === "4433281161") {
          // 60 day supply - 10% discount
          volumeDiscountMultiplier = 0.9;
          hasVolumeDiscount = true;
        } else if (sellingPlanId === "4433313929") {
          // 90 day supply - 20% discount
          volumeDiscountMultiplier = 0.8;
          hasVolumeDiscount = true;
        }

        // Determine applicable discount (promo not valid for 60/90 day plans)
        let applicableDiscount = discountInfo;
        if (discountInfo && discountInfo.type === "promo" && isVolumePlan) {
          const fallbackDiscount =
            discountInfo.practitionerFallback ||
            this.getPractitionerDiscountInfo();
          if (fallbackDiscount) {
            applicableDiscount = fallbackDiscount;
            console.log(
              "Item",
              index,
              "promo not valid for volume plan, using practitioner fallback"
            );
          } else {
            console.log(
              "Item",
              index,
              "promo not valid for volume plan and no practitioner discount, skipping"
            );
            return;
          }
        }

        // Get current line price from the first wrapper
        const priceElement = priceWrappers[0].querySelector(
          ".price price-money bdi, .price--end price-money bdi"
        );
        if (priceElement) {
          const priceText = priceElement.textContent.trim();
          currentLinePrice = Math.round(
            parseFloat(priceText.replace(/[^0-9.]/g, "")) * 100
          );
        }

        if (hasVolumeDiscount && currentLinePrice) {
          // Calculate true original unit price by reversing the volume discount
          const volumeDiscountedUnitPrice = Math.round(
            currentLinePrice / quantity
          );
          originalUnitPrice = Math.round(
            volumeDiscountedUnitPrice / volumeDiscountMultiplier
          );
        } else if (currentLinePrice) {
          // No volume discount - current price is the original
          originalUnitPrice = Math.round(currentLinePrice / quantity);
        }

        if (!currentLinePrice || !originalUnitPrice) {
          console.log(
            "Item",
            index,
            "missing prices - current:",
            currentLinePrice,
            "original:",
            originalUnitPrice
          );
          return;
        }

        // Calculate true original line price (before any discounts)
        const trueOriginalLinePrice = originalUnitPrice * quantity;
        console.log(
          "Item",
          index,
          "prices - original total:",
          trueOriginalLinePrice,
          "current:",
          currentLinePrice
        );

        // Calculate practitioner/promo discounted price
        const discountedLinePrice = this.calculateDiscountedPrice(
          currentLinePrice,
          applicableDiscount
        );
        console.log("Item", index, "discounted price:", discountedLinePrice);

        // Only show discount if there's an actual difference
        if (discountedLinePrice >= currentLinePrice) {
          console.log("Item", index, "no discount difference, skipping");
          return;
        }

        // Mark as processed
        cartItem.dataset.discountProcessed = "true";
        console.log("Item", index, "processing discount display");

        // Update ALL price wrappers (mobile and desktop)
        priceWrappers.forEach((priceWrapper, wrapperIndex) => {
          console.log("Item", index, "updating wrapper", wrapperIndex);

          if (hasVolumeDiscount) {
            // Item has volume discount - show: Original (strikethrough) -> Final
            const priceContainer = priceWrapper.querySelector(
              ".price, .price--end"
            );
            console.log(
              "Item",
              index,
              "wrapper",
              wrapperIndex,
              "price container found:",
              priceContainer
            );
            if (priceContainer) {
              console.log(
                "Item",
                index,
                "wrapper",
                wrapperIndex,
                "updating HTML with volume + promo discount display"
              );
              priceContainer.innerHTML = `
              <span class="price-diagonal-strike">
                <price-money class="discount-price">${this.formatMoney(
                  trueOriginalLinePrice
                )}</price-money>
              </span>
              <price-money class="discount-price">${this.formatMoney(
                discountedLinePrice
              )}</price-money>
            `;
              console.log(
                "Item",
                index,
                "wrapper",
                wrapperIndex,
                "HTML updated successfully (with volume discount)"
              );
            }
          } else {
            // No volume discount, just show original (strikethrough) -> final
            const priceContainer = priceWrapper.querySelector(
              ".price, .price--end"
            );
            console.log(
              "Item",
              index,
              "wrapper",
              wrapperIndex,
              "price container found:",
              priceContainer
            );
            if (priceContainer) {
              console.log(
                "Item",
                index,
                "wrapper",
                wrapperIndex,
                "updating HTML with discount display"
              );
              priceContainer.innerHTML = `
              <span class="price-diagonal-strike">
                <price-money class="discount-price">${this.formatMoney(
                  currentLinePrice
                )}</price-money>
              </span>
              <price-money class="discount-price">${this.formatMoney(
                discountedLinePrice
              )}</price-money>
            `;
              console.log(
                "Item",
                index,
                "wrapper",
                wrapperIndex,
                "HTML updated successfully"
              );
            } else {
              console.log(
                "Item",
                index,
                "wrapper",
                wrapperIndex,
                "ERROR: price container not found"
              );
            }
          }
        });
      } catch (error) {
        console.error("Error applying cart discount to item:", error);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new DiscountPriceDisplay();
  });
} else {
  new DiscountPriceDisplay();
}
